import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import {
  X, RefreshCw, Globe, Mail, Phone, MapPin, Building2, UserCheck,
  Calendar, FileText, Briefcase, Edit2, Check, ExternalLink, Hash, Banknote,
  Trash2, Star
} from "lucide-react";

const fmt = (n) => (n == null ? "—" : new Intl.NumberFormat("it-IT").format(n));
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT") : "—";

const Field = ({ icon: Icon, label, value, mono, copy }) => (
  <div style={{padding:"10px 0",borderBottom:"1px solid var(--mv-border)"}}>
    <div className="flex items-center gap-2 text-small text-muted" style={{marginBottom:4}}>
      {Icon && <Icon size={12}/>} {label}
    </div>
    <div style={{fontWeight:500,fontSize:14,fontFamily:mono?"ui-monospace, SFMono-Regular, monospace":"inherit",wordBreak:"break-word"}}>
      {value || <span className="text-muted">—</span>}
    </div>
  </div>
);

const EditableField = ({ label, icon: Icon, value, placeholder, onSave, type = "text", linkable = false }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  useEffect(() => setVal(value || ""), [value]);
  const save = async () => { await onSave(val); setEditing(false); };
  return (
    <div style={{padding:"10px 0",borderBottom:"1px solid var(--mv-border)"}}>
      <div className="flex items-center gap-2 text-small text-muted" style={{marginBottom:4}}>
        {Icon && <Icon size={12}/>} {label}
      </div>
      {editing ? (
        <div className="flex gap-2 items-center">
          <input className="input" type={type} value={val} onChange={e=>setVal(e.target.value)} placeholder={placeholder}
            style={{padding:"6px 10px"}} autoFocus
            onKeyDown={e=>{if(e.key==="Enter") save(); if(e.key==="Escape") setEditing(false);}}/>
          <button onClick={save} className="btn btn-primary btn-sm"><Check size={14}/></button>
          <button onClick={()=>{setEditing(false); setVal(value||"");}} className="btn btn-ghost btn-sm"><X size={14}/></button>
        </div>
      ) : (
        <div className="flex items-center gap-2" style={{minHeight:24}}>
          <div style={{flex:1,fontWeight:500,fontSize:14,wordBreak:"break-word"}}>
            {value ? (linkable ? <a href={linkable === "url" ? `https://${value.replace(/^https?:\/\//,"")}` : linkable === "mail" ? `mailto:${value}` : linkable === "tel" ? `tel:${value}` : "#"} target="_blank" rel="noreferrer">{value}</a> : value) : <span className="text-muted">— vuoto —</span>}
          </div>
          <button onClick={()=>setEditing(true)} className="btn btn-ghost btn-sm" title="Modifica"><Edit2 size={12}/></button>
        </div>
      )}
    </div>
  );
};

export default function CrmCompanyCard({ companyId, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState("anagrafica");

  const reload = async () => {
    const { data } = await api.get(`/crm/companies/${companyId}`);
    setData(data);
  };
  useEffect(() => { reload(); }, [companyId]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post(`/crm/companies/${companyId}/refresh`);
      await reload();
      if (onUpdated) onUpdated();
    } finally { setRefreshing(false); }
  };

  const saveField = async (field, value) => {
    await api.patch(`/crm/companies/${companyId}`, { [field]: value });
    await reload();
    if (onUpdated) onUpdated();
  };

  const toggleCustomer = async (isCustomer, contractValueEur, notes) => {
    await api.patch(`/crm/companies/${companyId}/customer`, {
      isCustomer, contractValueEur, notes,
    });
    await reload();
    if (onUpdated) onUpdated();
  };

  const deleteCompany = async () => {
    if (!window.confirm(
      `Eliminare definitivamente "${data?.company?.companyName}" dal CRM?\n\n` +
      `Verranno cancellate anche tutte le sue analisi, le membership nelle liste e le email inviate. ` +
      `L'azione NON è reversibile.`
    )) return;
    try {
      await api.delete(`/crm/companies/${companyId}`);
      if (onUpdated) onUpdated();
      onClose();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  if (!data) return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
        <div style={{padding:40,textAlign:"center"}}><span className="spinner"/></div>
      </div>
    </div>
  );

  const c = data.company;
  const memberships = data.memberships || [];
  const raw = c.rawData || {};
  const rawM = c.rawMarketing || {};

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e=>e.stopPropagation()} style={{maxWidth:1000,maxHeight:"92vh"}} data-testid="crm-card">
        <div className="flex justify-between items-start mb-4" style={{position:"sticky",top:0,background:"white",paddingBottom:14,borderBottom:"1px solid var(--mv-border)",zIndex:5}}>
          <div style={{flex:1}}>
            <div className="text-small text-muted" style={{letterSpacing:".08em",textTransform:"uppercase",fontWeight:600,marginBottom:4}}>{c.legalForm || "Azienda"}</div>
            <div className="h-section" data-testid="crm-card-name">{c.companyName}</div>
            <div className="text-small text-muted mt-2">
              P.IVA {c.vatCode}{c.taxCode && c.taxCode !== c.vatCode ? ` · CF ${c.taxCode}` : ""} ·
              {c.city ? ` ${c.city} (${c.province})` : ""}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={refresh} disabled={refreshing} className="btn btn-secondary btn-sm" data-testid="crm-refresh">
              {refreshing ? <span className="spinner"/> : <><RefreshCw size={14}/> Aggiorna da OpenAPI</>}
            </button>
            <button onClick={deleteCompany} className="btn btn-ghost btn-sm"
              title="Elimina azienda dal CRM (cascade)"
              data-testid="crm-delete"
              style={{color:"var(--mv-danger)"}}>
              <Trash2 size={14}/>
            </button>
            <button onClick={onClose} className="btn btn-ghost btn-sm" data-testid="crm-close"><X size={18}/></button>
          </div>
        </div>

        {/* Customer banner */}
        <CustomerBanner company={c} onToggle={toggleCustomer}/>

        <div className="tabs">
          <div onClick={()=>setTab("anagrafica")} className={`tab ${tab==="anagrafica"?"active":""}`}>Anagrafica</div>
          <div onClick={()=>setTab("contatti")} className={`tab ${tab==="contatti"?"active":""}`}>Contatti & Web</div>
          <div onClick={()=>setTab("economici")} className={`tab ${tab==="economici"?"active":""}`}>Economici</div>
          <div onClick={()=>setTab("persone")} className={`tab ${tab==="persone"?"active":""}`}>Persone</div>
          <div onClick={()=>setTab("attivita")} className={`tab ${tab==="attivita"?"active":""}`}>Attività CRM ({memberships.length})</div>
        </div>

        {tab === "anagrafica" && (
          <div className="grid gap-6" style={{gridTemplateColumns:"1fr 1fr"}}>
            <div>
              <Field icon={Hash} label="Partita IVA" value={c.vatCode} mono/>
              <Field icon={Hash} label="Codice fiscale" value={c.taxCode} mono/>
              <Field icon={Building2} label="Forma giuridica" value={c.legalForm}/>
              <Field icon={Briefcase} label="Codice ATECO" value={c.ateco ? `${c.ateco} · ${c.atecoDescr || ""}` : null}/>
              <Field label="Stato attività" value={c.activityStatus}/>
              <Field icon={Calendar} label="Data iscrizione" value={fmtDate(c.registrationDate)}/>
            </div>
            <div>
              <Field icon={MapPin} label="Indirizzo" value={c.address}/>
              <Field label="Città" value={c.city ? `${c.city} (${c.province})` : null}/>
              <Field label="CAP" value={c.zipCode}/>
              <Field label="Regione" value={c.region}/>
              <Field icon={Hash} label="Codice Belfiore" value={c.townCode} mono/>
              <Field label="CCIAA / REA" value={c.cciaa ? `${c.cciaa} · ${c.rea || "—"}` : null}/>
              <Field icon={Hash} label="Codice SDI" value={c.sdiCode} mono/>
            </div>
          </div>
        )}

        {tab === "contatti" && (
          <div>
            <div className="text-small text-muted mb-4">
              I campi qui sotto sono <strong>modificabili</strong>. Cliccando sull'icona ✏️ puoi correggere manualmente sito, email, telefono o aggiungere note.
              {c.websiteSource === "deduced_from_email" && (
                <div className="badge badge-orange mt-2" style={{display:"inline-block",padding:"4px 10px"}}>
                  💡 Il sito è stato dedotto dal dominio dell'email. Verifica che sia corretto.
                </div>
              )}
            </div>
            <EditableField icon={Globe} label="Sito web" value={c.website} placeholder="esempio.it (senza https://)"
              onSave={v=>saveField("website", v)} linkable="url"/>
            <EditableField icon={Mail} label="Email aziendale" value={c.email} placeholder="info@esempio.it"
              onSave={v=>saveField("email", v)} type="email" linkable="mail"/>
            <EditableField icon={Mail} label="PEC" value={c.pec} placeholder="pec@dominio.pec.it"
              onSave={v=>saveField("pec", v)} type="email" linkable="mail"/>
            <EditableField icon={Phone} label="Telefono" value={c.phone} placeholder="+39 02 1234567"
              onSave={v=>saveField("phone", v)} type="tel" linkable="tel"/>
            <EditableField icon={FileText} label="Note CRM (libere)" value={c.notes} placeholder="Es. 'Chiamato il 18/05, richiamare giovedì'"
              onSave={v=>saveField("notes", v)}/>
            {c.socialLinks && Object.keys(c.socialLinks).length > 0 && (
              <div style={{padding:"10px 0",borderBottom:"1px solid var(--mv-border)"}}>
                <div className="flex items-center gap-2 text-small text-muted" style={{marginBottom:6}}>Social</div>
                <div className="flex gap-2" style={{flexWrap:"wrap"}}>
                  {Object.entries(c.socialLinks).map(([k, v]) => (
                    <a key={k} href={v} target="_blank" rel="noreferrer" className="badge badge-brand">{k} <ExternalLink size={10} style={{marginLeft:4}}/></a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "economici" && (
          <div className="grid gap-6" style={{gridTemplateColumns:"1fr 1fr"}}>
            <div>
              <Field icon={Banknote} label="Fatturato (ultimo bilancio)" value={c.revenue ? `€ ${fmt(c.revenue)}` : null}/>
              <Field label="Dipendenti" value={c.employees}/>
            </div>
            <div>
              {(raw.balanceSheets?.last) && (
                <>
                  <Field label="Anno bilancio" value={raw.balanceSheets.last.year}/>
                  <Field label="Capitale sociale" value={raw.balanceSheets.last.shareCapital ? `€ ${fmt(raw.balanceSheets.last.shareCapital)}` : null}/>
                  <Field label="Patrimonio netto" value={raw.balanceSheets.last.netWorth ? `€ ${fmt(raw.balanceSheets.last.netWorth)}` : null}/>
                </>
              )}
            </div>
          </div>
        )}

        {tab === "persone" && (
          <div>
            {c.owner && (
              <div className="card mb-4" style={{padding:14,background:"var(--mv-bg)",borderLeft:"4px solid var(--mv-primary)"}}>
                <div className="text-small text-muted" style={{letterSpacing:".06em",textTransform:"uppercase",fontWeight:600,marginBottom:6}}>
                  <UserCheck size={12} style={{display:"inline",verticalAlign:"middle"}}/> Legale rappresentante
                </div>
                <div style={{fontWeight:700,fontSize:18}}>{c.owner.fullName}</div>
                <div className="text-small text-muted mt-2">{c.owner.role}</div>
              </div>
            )}
            {c.managers && c.managers.length > 0 ? (
              <table className="tbl">
                <thead><tr><th>Nome</th><th>Cognome</th><th>Ruolo</th><th>Legale rappr.</th></tr></thead>
                <tbody>
                  {c.managers.map((m, i) => (
                    <tr key={i}>
                      <td>{m.name || "—"}</td>
                      <td>{m.surname || "—"}</td>
                      <td className="text-small">{(m.roles || []).map(r => r.role?.description).filter(Boolean).join(", ") || "—"}</td>
                      <td>{m.isLegalRepresentative ? <span className="badge badge-green">Sì</span> : <span className="text-muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-muted text-small">
                Nessun dato sui titolari ancora caricato.
                Clicca <strong>"Aggiorna da OpenAPI"</strong> per recuperare la lista titolari/amministratori.
              </div>
            )}
          </div>
        )}

        {tab === "attivita" && (
          <div>
            {memberships.length === 0 ? (
              <div className="text-muted">Questa azienda non è in nessuna lista. Aggiungila da Ricerca.</div>
            ) : (
              memberships.map(m => (
                <div key={m.item.id} className="card mb-3" style={{padding:14}}>
                  <div className="flex justify-between items-center mb-2">
                    <Link to={`/liste/${m.list?.id}`} style={{fontWeight:600}}>{m.list?.name || "(lista rimossa)"}</Link>
                    <span className="text-small text-muted">{fmtDate(m.item.createdAt)}</span>
                  </div>
                  <div className="flex gap-2" style={{flexWrap:"wrap"}}>
                    <span className="badge badge-gray">Stato: {m.item.status}</span>
                    {m.item.trafficLight && <span className={`badge badge-${m.item.trafficLight === "target" || m.item.trafficLight === "green" ? "green" : m.item.trafficLight === "skip" || m.item.trafficLight === "red" ? "red" : "orange"}`}>{m.item.trafficLight}</span>}
                    {m.analysis?.status === "completed" && (
                      <a href={`/analisi/${m.analysis.publicToken}`} target="_blank" rel="noreferrer" className="badge badge-brand">
                        Score {m.analysis.summary?.score_globale}/100 <ExternalLink size={10} style={{marginLeft:4}}/>
                      </a>
                    )}
                    {m.email && <span className="badge badge-brand">Email {m.email.status} il {fmtDate(m.email.sentAt)}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function CustomerBanner({ company, onToggle }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(company.contractValueEur || "");
  const [notes, setNotes] = useState(company.customerNotes || "");

  if (company.isCustomer && !editing) {
    return (
      <div className="card mb-4" style={{padding: 14, background: "#dcfce7", borderLeft: "4px solid var(--mv-success)"}} data-testid="customer-banner">
        <div className="flex items-center justify-between" style={{flexWrap:"wrap",gap:8}}>
          <div className="flex items-center gap-2">
            <Star size={18} color="var(--mv-success)" fill="var(--mv-success)"/>
            <div>
              <div style={{fontWeight: 600, color: "#15803d"}}>Cliente acquisito</div>
              <div className="text-small text-muted">
                {company.contractValueEur != null && <>Contratto: <strong>€ {new Intl.NumberFormat("it-IT").format(company.contractValueEur)}</strong> · </>}
                Da {fmtDate(company.customerSince)}
                {company.customerNotes && <> · {company.customerNotes}</>}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={()=>setEditing(true)} className="btn btn-ghost btn-sm" data-testid="customer-edit">
              <Edit2 size={12}/> Modifica
            </button>
            <button onClick={()=>{
              if (window.confirm("Rimuovere il flag cliente da questa azienda?")) onToggle(false);
            }} className="btn btn-ghost btn-sm" style={{color: "var(--mv-danger)"}} data-testid="customer-unmark">
              Rimuovi
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (editing || !company.isCustomer) {
    const isInitial = !company.isCustomer;
    return (
      <div className="card mb-4" style={{padding: 14, background: isInitial ? "#fff7ed" : "#fef3c7", borderLeft: "4px solid var(--mv-primary)"}}>
        <div className="flex items-start gap-2 mb-3">
          <Star size={16} color="var(--mv-primary)" style={{marginTop:2}}/>
          <div className="flex-1">
            <div style={{fontWeight: 600}}>{isInitial ? "Segna come cliente acquisito" : "Modifica dati cliente"}</div>
            <div className="text-small text-muted">
              Imposta il valore del contratto per calcolare ROI e CAC nella dashboard sorgenti.
            </div>
          </div>
        </div>
        <div className="grid gap-2" style={{gridTemplateColumns:"1fr 2fr"}}>
          <div>
            <label className="label text-small">Valore contratto €</label>
            <input className="input" type="number" step="0.01" placeholder="3500"
              value={val} onChange={e=>setVal(e.target.value)}
              data-testid="customer-value"/>
          </div>
          <div>
            <label className="label text-small">Note (opzionale)</label>
            <input className="input" placeholder="es. Pacchetto SEO 12 mesi · Firmato 18/05/2026"
              value={notes} onChange={e=>setNotes(e.target.value)}/>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={async ()=>{
            await onToggle(true, val ? parseFloat(val) : null, notes);
            setEditing(false);
          }} className="btn btn-primary btn-sm" data-testid="customer-save">
            <Check size={14}/> {isInitial ? "Conferma cliente" : "Salva"}
          </button>
          {!isInitial && (
            <button onClick={()=>setEditing(false)} className="btn btn-ghost btn-sm">Annulla</button>
          )}
        </div>
      </div>
    );
  }
  return null;
}
